import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme, Dialog as ImportedDialog, DialogContent } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import RoundButton from '../RoundButton/RoundButton';
import useStyles from './Modal.styles';
import _debounce from 'lodash-es/debounce';
// import awaitInteractionResponse from 'await-interaction-response'
const SCROLL_DEBOUNCE_DELAY_MS = 100;
const restoreDocumentScroll = (currentScrollPosition) => {
    var _a;
    if (globalThis.document.body.classList.contains('modalOpen')) {
        globalThis.document.body.classList.remove('modalOpen');
        window.scrollTo({ top: (_a = currentScrollPosition.current) !== null && _a !== void 0 ? _a : 0 });
    }
};
const closeModal = ({ isFromPDPAlternateFlowModal, setOpen, onClose, isLargeDesktopAndBelow, currentScrollPosition, }) => {
    if (!isFromPDPAlternateFlowModal) {
        setOpen(false);
    }
    onClose === null || onClose === void 0 ? void 0 : onClose();
    if (isLargeDesktopAndBelow) {
        restoreDocumentScroll(currentScrollPosition);
    }
};
const useModalScrollLock = ({ open, isLargeDesktopAndBelow, isStickyHeader, currentScrollPosition, }) => {
    useEffect(() => {
        if (!(open && isLargeDesktopAndBelow && !isStickyHeader)) {
            return;
        }
        currentScrollPosition.current = window.scrollY;
        window.document.body.classList.add('modalOpen');
        return () => {
            if (isLargeDesktopAndBelow) {
                restoreDocumentScroll(currentScrollPosition);
            }
        };
    }, [open, isLargeDesktopAndBelow, isStickyHeader, currentScrollPosition]);
};
const useStickyHeaderState = (isStickyHeader, open) => {
    const [isSticky, setSticky] = useState(false);
    useEffect(() => {
        if (!(isStickyHeader && open)) {
            return;
        }
        const sticky = 20;
        const bodyContainer = document.querySelector('#bodyContainer');
        const handleScroll = _debounce(() => {
            const header = (bodyContainer === null || bodyContainer === void 0 ? void 0 : bodyContainer.scrollTop) || 0;
            setSticky(header > sticky);
        }, SCROLL_DEBOUNCE_DELAY_MS);
        const timeoutId = globalThis.setTimeout(() => {
            bodyContainer === null || bodyContainer === void 0 ? void 0 : bodyContainer.addEventListener('scroll', handleScroll);
        }, 10);
        return () => {
            globalThis.clearTimeout(timeoutId);
            bodyContainer === null || bodyContainer === void 0 ? void 0 : bodyContainer.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [isStickyHeader, open]);
    return isSticky;
};
const useScrollTitleIntoView = (isFromPccOccasion, titleRef) => {
    useEffect(() => {
        var _a;
        if (isFromPccOccasion) {
            (_a = titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
        }
    }, [isFromPccOccasion, titleRef]);
};
const getModalTitle = ({ isStickyHeader, shouldRenderTitle, isFromCategory, title, headerTitleClassName, }) => {
    if (isStickyHeader) {
        return title;
    }
    if (!shouldRenderTitle) {
        return null;
    }
    return isFromCategory ? _jsx("div", { className: headerTitleClassName, children: title }) : _jsx("h3", { children: title });
};
const getPaperClassName = ({ classes, isFromPDPAlternateFlowModal, isAlternateFlowSignInModal, isStickyHeader, isFromPccOccasion, isCollectionModalDesktop, }) => {
    return clsx(classes.modal, isFromPDPAlternateFlowModal ? classes.pdpAlternateFlowModal : '', isAlternateFlowSignInModal ? classes.alternateFlowSignInModal : '', isStickyHeader ? classes.stickyHeaderContainer : '', isFromPccOccasion ? classes.pccModalContainer : '', isCollectionModalDesktop && classes.collectionModal);
};
const getDialogContentClassName = ({ classes, isFromPDPAlternateFlowModal, isStickyHeader, isCalendarOpen, }) => {
    return clsx(isFromPDPAlternateFlowModal ? classes.contentContainerRaw : classes.contentContainer, isStickyHeader && !isCalendarOpen ? classes.stickyContentContainer : '');
};
const ModalHeader = ({ classes, titleRef, shouldRenderTitle, isAlternateFlowSignInModal, isFromImageGallery, isStickyHeader, isMobileDatePicker, isSticky, isMediaGallery, isFromCategory, title, isFromPDPAlternateFlowModal, handleClose, customStickyBtnClass, }) => {
    return (_jsxs("header", { ref: titleRef, className: clsx(shouldRenderTitle ? classes.header : classes.noTitleHeader, isAlternateFlowSignInModal ? classes.alternateFlowSignInModalHeader : '', isFromImageGallery ? classes.imageGalleryHeader : '', isStickyHeader ? classes.stickyHeader : '', isMobileDatePicker ? classes.pccDatepicker : '', isSticky ? classes.stickyHeaderBorder : '', isMediaGallery ? classes.mediaGalleryHeader : ''), children: [getModalTitle({
                isStickyHeader,
                shouldRenderTitle,
                isFromCategory,
                title,
                headerTitleClassName: classes.headerTitle,
            }), !isFromPDPAlternateFlowModal && (_jsx(RoundButton, { icon: "close", ariaLabel: "Close", onClick: handleClose, className: clsx(isStickyHeader ? classes.stickyButtonContainer : '', customStickyBtnClass ? classes[customStickyBtnClass] : '') }))] }));
};
const Modal = ({ open, children, title, setOpen, keepMounted, onClose, onEnter, fullWidth, fullScreen, maxWidth, disablePortal, noPadding, container, className, isFromImageGallery, isStickyHeader, customStickyBtnClass, isCalendarOpen, isFromPccOccasion, isMobileDatePicker, isFromCategory, isMediaGallery, isCollectionModalDesktop, isFromPDPAlternateFlowModal = false, isAlternateFlowSignInModal = false, }) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const isLargeDesktopAndBelow = useMediaQuery({ query: `(max-width: ${theme.breakpoints.values.xl}px)` });
    const isDesktop = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.md}px)` });
    const isDesktopAlternateFlowSignInModal = isAlternateFlowSignInModal && isDesktop;
    const currentScrollPosition = useRef(undefined);
    const titleRef = useRef(null);
    const shouldRenderTitle = !!title && !isDesktopAlternateFlowSignInModal;
    useModalScrollLock({
        open,
        isLargeDesktopAndBelow,
        isStickyHeader,
        currentScrollPosition,
    });
    const isSticky = useStickyHeaderState(isStickyHeader, open);
    useScrollTitleIntoView(isFromPccOccasion, titleRef);
    const handleClose = () => {
        // await awaitInteractionResponse()
        closeModal({
            isFromPDPAlternateFlowModal,
            setOpen,
            onClose,
            isLargeDesktopAndBelow,
            currentScrollPosition,
        });
    };
    return (_jsxs(ImportedDialog, { className: clsx(className !== null && className !== void 0 ? className : '', noPadding && classes.noPadding), open: open, disablePortal: disablePortal, keepMounted: keepMounted, onClose: handleClose, onTransitionEnter: onEnter, fullWidth: fullWidth, fullScreen: fullScreen, maxWidth: maxWidth, disableAutoFocus: true, scroll: "paper", PaperProps: {
            className: getPaperClassName({
                classes,
                isFromPDPAlternateFlowModal,
                isAlternateFlowSignInModal,
                isStickyHeader,
                isFromPccOccasion,
                isCollectionModalDesktop,
            }),
        }, container: container, children: [!isCollectionModalDesktop && (_jsx(ModalHeader, { classes: classes, titleRef: titleRef, shouldRenderTitle: shouldRenderTitle, isAlternateFlowSignInModal: isAlternateFlowSignInModal, isFromImageGallery: isFromImageGallery, isStickyHeader: isStickyHeader, isMobileDatePicker: isMobileDatePicker, isSticky: isSticky, isMediaGallery: isMediaGallery, isFromCategory: isFromCategory, title: title, isFromPDPAlternateFlowModal: isFromPDPAlternateFlowModal, handleClose: handleClose, customStickyBtnClass: customStickyBtnClass })), _jsx(DialogContent, { className: getDialogContentClassName({
                    classes,
                    isFromPDPAlternateFlowModal,
                    isStickyHeader,
                    isCalendarOpen,
                }), id: "bodyContainer", children: children })] }));
};
export default Modal;
